import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus
} from '@azure/msal-browser';
import { filter, firstValueFrom } from 'rxjs';

import { ZendeskService } from '@zendesk/services/zendesk.service';

@Injectable()
export class AuthService {
  readonly #authService = inject(MsalService);
  readonly #broadcastService = inject(MsalBroadcastService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #zendeskService = inject(ZendeskService);

  // DOCS
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-samples
  // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-angular-samples/angular-standalone-sample/src/app/app.component.ts

  async init(): Promise<void> {
    // Subscribing to handleRedirectObservable before any other functions
    // both initializes the application and ensures redirects are handled
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/redirects.md#redirects-with-standalone-components
    await firstValueFrom(this.#authService.handleRedirectObservable());

    this.#authService.instance.enableAccountStorageEvents();

    this.#broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.#authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.#broadcastService.inProgress$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  signOut(): void {
    this.#zendeskService.logoutUser();
    this.#authService.logoutRedirect();
  }

  getActiveAccount(): AccountInfo | null {
    return this.#authService.instance.getActiveAccount();
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.#authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.#authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.#authService.instance.getAllAccounts();
      this.#authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
